import { localizedAccountDisplayName, uniqueDisplayAccount } from "../../utils/localizedAccountDisplayName";
// src/pages/prepaids/PrepaidDetail.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ParseKeys } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import { Button } from "../../components/Button";
import { AppleComboBox } from "../../components/AppleComboBox";
import { AppleNumberInput } from "../../components/AppleNumberInput";
import { PurchaseInfoHeader } from "../../components/PurchaseInfoHeader";
import { apiDelete, apiGet, apiPost } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import { formatNumber, formatMoney } from "../../utils/formatNumber";
import {
  ymdTodayLocal,
  formatMMDDYYYYFromYMD,
  mdyToYmd,
  normalizeMDY,
  formatAnyDateToLocale,
} from "../../utils/dateUtils";
import { A } from "../../styles/appleClasses";
import MemoSection from "../../components/MemoSection";
import {
  isClosedPeriodError,
  isDateClosed,
} from "../../utils/closedPeriod";
import {
  IDLE_PREPAID_EXPENSE_ATTEMPT,
  PrepaidExpenseAttempt,
  canonicalPrepaidExpense,
  fingerprintPrepaidExpense,
  randomUuidKey,
  decidePrepaidExpenseAttempt,
  resolvePrepaidExpenseSuccess,
  resolvePrepaidExpenseDefiniteRejection,
  resolvePrepaidExpenseAmbiguousFailure,
  shouldRestoreAmbiguousAttempt as shouldRestoreAmbiguousExpenseAttempt,
  classifyPrepaidExpenseFailure,
} from "../../utils/prepaidExpenseIdempotency";

type PrepaidItem = {
  id: number;
  date: string; // YYYY-MM-DD
  vendor_name?: string | null;
  bank_name?: string | null;
  bank_account_id?: number | null;
  memo?: string | null;
  original_amount: number;
  remaining_amount: number;
  status?: string | null; // open/closed/voided
};

type Account = {
  id: number | string;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

type PrepaidEvent = {
  id: number;
  prepaid_id: number;
  kind: string; // "expense" | "refund" | "writeoff"
  date: string; // YYYY-MM-DD (or ISO)
  amount: number;
  expense_account_id?: number | null;
  bank_account_id?: number | null;
  journal_entry_id?: number | null;
};

const lower = (s?: string | null) => (s || "").toLowerCase();
type DetailError =
  | { source: "external"; text: string }
  | { source: "local"; key: ParseKeys; closedThroughDate?: string | null };

// Identity marks only this locally produced failure, never identical API text.
class PrepaidNotFoundError extends Error {}
const localError = (key: ParseKeys): DetailError => ({ source: "local", key });
function detailError(e: any, fallback: ParseKeys): DetailError {
  if (e instanceof PrepaidNotFoundError) return localError("prepaidDetail.notFound");
  const text = niceDetail(e?.response?.data?.detail) || e?.message;
  return text ? { source: "external", text } : { source: "local", key: fallback };
}

function money(n: number | undefined | null) {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return formatMoney(n);
}

async function fetchPrepaid(
  company_id: string,
  prepaid_id: string,
): Promise<PrepaidItem> {
  const list = await apiGet<PrepaidItem[]>(
    `/prepaids?company_id=${company_id}`,
  );
  const found = (list || []).find((x) => String(x.id) === String(prepaid_id));
  if (!found) throw new PrepaidNotFoundError("Prepaid not found");
  return found;
}

async function fetchPrepaidEventsBestEffort(
  prepaid_id: string,
): Promise<PrepaidEvent[]> {
  return await apiGet<PrepaidEvent[]>(`/prepaids/${prepaid_id}/events`);
}

function niceDetail(d: any): string {
  if (!d) return "";
  if (Array.isArray(d)) {
    return d.map((x) => `${(x.loc || []).join(".")}: ${x.msg}`).join(" | ");
  }
  return String(d);
}

type DisplayStatus = "open" | "partial" | "closed" | "voided";

function displayStatusFor(p?: PrepaidItem | null): DisplayStatus {
  if (!p) return "open";
  const st = lower(p.status || "open");
  const orig = Number(p.original_amount || 0);
  const rem = Number(p.remaining_amount || 0);

  if (st === "voided") return "voided";
  if (rem <= 0 || st === "closed") return "closed";
  if (orig > 0 && rem > 0 && rem < orig) return "partial";
  return "open";
}

/** PurchaseInfoHeader bubbleType mapping (keeps types clean; no "open" hack) */
type HeaderBubbleType = "paid" | "partial" | "unpaid" | "voided";
const bubbleForStatus = (
  ds: DisplayStatus,
): { label: string; bubbleType: HeaderBubbleType } => {
  if (ds === "voided") return { label: "Voided", bubbleType: "voided" };
  if (ds === "closed") return { label: "Closed", bubbleType: "paid" };
  if (ds === "partial") return { label: "Partial", bubbleType: "partial" };
  return { label: "Open", bubbleType: "unpaid" };
};

// Apple small button override for your large default Button.tsx
const btnSmBase =
  "h-10 px-4 py-0 rounded-xl text-[13px] font-medium shadow-sm " +
  "inline-flex items-center justify-center leading-none";

const btnSmPrimary = `${btnSmBase} min-w-[120px]`;
const btnSmSecondary =
  `${btnSmBase} min-w-[120px] ` +
  "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-200";

const btnSmDanger =
  `${btnSmBase} min-w-[120px] ` +
  "bg-red-600 hover:bg-red-700 active:bg-red-800 border border-red-600 focus:ring-red-200";

export default function PrepaidDetail() {
  const { t } = useTranslation();
  const renderError = (error: DetailError): string => {
    if (error.source === "external") return error.text;
    switch (error.key) {
      case "prepaidDetail.notFound": return t("prepaidDetail.notFound");
      case "prepaidDetail.loadError": return t("prepaidDetail.loadError");
      case "prepaidDetail.positiveAmount": return t("prepaidDetail.positiveAmount");
      case "prepaidDetail.invalidDate": return t("prepaidDetail.invalidDate");
      case "prepaidDetail.applyError": return t("prepaidDetail.applyError");
      case "prepaidDetail.editedRetry": return t("prepaidDetail.editedRetry");
      case "prepaidDetail.conflict": return t("prepaidDetail.conflict");
      case "prepaidDetail.voidError": return t("prepaidDetail.voidError");
      case "depositDetail.selectExpenseError": return t("depositDetail.selectExpenseError");
      case "depositDetail.amountExceeds": return t("depositDetail.amountExceeds");
      case "depositDetail.memoError": return t("depositDetail.memoError");
      case "sales.errors.closedEdit": return t("sales.errors.closedEdit");
      case "sales.errors.closedSubmit": return t("sales.errors.closedSubmit", {
        date: error.closedThroughDate ? formatAnyDateToLocale(error.closedThroughDate.slice(0, 10)) : t("sales.errors.closedDate"),
      });
      default: throw new Error("Unexpected local prepaid error identity");
    }
  };
  const navigate = useNavigate();
  const { company_id = "", prepaid_id = "" } = useParams<{
    company_id: string;
    prepaid_id: string;
  }>();

  const [prepaid, setPrepaid] = useState<PrepaidItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<DetailError | null>(null);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );

  // Memo (editable)
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoDraft, setMemoDraft] = useState("");
  const [memoSaving, setMemoSaving] = useState(false);
  const [memoError, setMemoError] = useState<DetailError | null>(null);

  // Accounts
  const [accounts, setAccounts] = useState<Account[]>([]);
  const accountNameById = useMemo(() => {
    const m = new Map<number, string>();
    (accounts || []).forEach((a) => {
      const idNum = Number(a.id);
      if (!Number.isNaN(idNum)) m.set(idNum, lower(a.kind) === "credit_card_payable" ? t("expenses.entry.creditCard") : a.name || "");
    });
    return m;
  }, [accounts, t]);

  const expenseAccounts = useMemo(() => {
    return (accounts || [])
      .filter((a) => lower(a.type) === "expense" && !a.is_system)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts]);

  // History
  const [events, setEvents] = useState<PrepaidEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<
    { source: "external"; text: string } | { source: "local" } | null
  >(null);
  const [eventsLoadedOnce, setEventsLoadedOnce] = useState(false);

  // Apply -> Expense only
  const [applyDate, setApplyDate] = useState<string>(() =>
    formatMMDDYYYYFromYMD(ymdTodayLocal()),
  );
  const [applyAmount, setApplyAmount] = useState("");
  const [expenseAcc, setExpenseAcc] = useState<Account | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<DetailError | null>(null);
  const [backendRejectsApplyDate, setBackendRejectsApplyDate] = useState(false);

  // Prepaid recognition ("Apply") idempotency attempt lifecycle (see
  // utils/prepaidExpenseIdempotency.ts). A ref holding a Map, not state:
  // must be stable across renders, must not itself trigger a re-render, and
  // must be readable synchronously by the in-flight guard below.
  //
  // ONE INDEPENDENT SLOT PER prepaid_id -- NOT one shared attempt for the
  // whole component. This component is mounted once for its route and
  // reused across different prepaid_id route-param values without
  // remounting, so an ambiguous attempt on Prepaid A must be retained
  // under A's own key while Prepaid B's key (absent from the map, or
  // resolved back to idle) starts fresh and is immediately usable -- never
  // blocked by A's unresolved state. A prepaid's entry is removed entirely
  // once its attempt resolves to idle (success or definite rejection), so
  // the map only ever holds in-flight/ambiguous attempts.
  const prepaidExpenseAttemptsRef = useRef<Map<number, PrepaidExpenseAttempt>>(
    new Map(),
  );
  const getPrepaidExpenseAttempt = (prepaidId: number): PrepaidExpenseAttempt =>
    prepaidExpenseAttemptsRef.current.get(prepaidId) ?? IDLE_PREPAID_EXPENSE_ATTEMPT;
  // Purely synchronous double-click/repeat-tap guard for whichever
  // prepaid's Apply button was just clicked -- independent of the
  // per-prepaid attempt map above. A single boolean is correct here (not a
  // per-prepaid map): only one handleApply invocation can ever be
  // executing at a time from user interaction, since only one prepaid's
  // form is rendered/clickable at once.
  const prepaidExpenseInFlightRef = useRef(false);

  // Delete UI (SaleDetail-style modal)
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<DetailError | null>(null);

  const remaining = Number(prepaid?.remaining_amount || 0);
  const original = Number(prepaid?.original_amount || 0);

  const ds = displayStatusFor(prepaid);
  const isVoided = ds === "voided";
  const isClosed = ds === "closed";
  const canApply = !!prepaid && !isClosed && !isVoided && remaining > 0;

  const deleteAvailable =
    !!prepaid &&
    !isVoided &&
    !isClosed &&
    Math.abs(original - remaining) < 0.000001 &&
    // if history is loaded and has events, disallow delete
    !(eventsLoadedOnce && events.length > 0);

  const statusBubbles = useMemo(() => {
    if (!prepaid) return [];
    const status = displayStatusFor(prepaid);
    const b = bubbleForStatus(status);
    const label = status === "voided" ? t("sales.list.voided")
      : status === "closed" ? t("spendingDeposits.statusClosed")
      : status === "partial" ? t("spendingDeposits.statusPartial")
      : t("spendingDeposits.statusOpen");
    return [{ ...b, label }];
  }, [prepaid, t]);

  const lines = useMemo(() => {
    if (!prepaid) return [];
    return [
      {
        id: prepaid.id,
        account:
          accountNameById.get(Number(prepaid.bank_account_id)) ||
          prepaid.bank_name ||
          "—",
        amount: original || 0,
      },
    ];
  }, [accountNameById, prepaid, original]);

  const historyRows = useMemo(() => {
    const eventLabel = (kind: string) => {
      const k = lower(kind);
      if (k === "expense") return t("prepaidHistory.apply");
      if (k === "refund") return t("depositDetail.refund");
      if (k === "writeoff") return t("prepaidHistory.writeoff");
      return kind || "—";
    };
    const xs = (events || []).slice();
    xs.sort((a, b) => {
      const da = String(a.date || "");
      const db = String(b.date || "");
      if (da < db) return -1;
      if (da > db) return 1;
      return (a.id || 0) - (b.id || 0);
    });

    return xs.map((ev) => {
      const k = lower(ev.kind);
      const acctId =
        k === "refund" ? ev.bank_account_id : ev.expense_account_id;
      const account = acctId != null
        ? accounts.find((a) => Number(a.id) === Number(acctId))
        : undefined;
      const acctName = lower(account?.kind) === "credit_card_payable"
        ? t("expenses.entry.creditCard")
        : account?.name;

      const d = String(ev.date || "").slice(0, 10);

      return {
        id: ev.id,
        date: formatAnyDateToLocale(d),
        action: eventLabel(ev.kind),
        account: acctName || "—",
        displayAccount: localizedAccountDisplayName(uniqueDisplayAccount(accounts, acctId), t, acctName || "—"),
        amount: Number(ev.amount || 0),
      };
    });
  }, [events, accounts, t]);

  const showHistorySection =
    eventsLoading ||
    historyRows.length > 0 ||
    (eventsError &&
      (eventsError.source === "local" || eventsError.text.trim().length > 0));

  useEffect(() => {
    if (!company_id || !prepaid_id) return;
    setLoading(true);
    setErr(null);

    fetchPrepaid(company_id, prepaid_id)
      .then((d) => setPrepaid(d || null))
      .catch((e: any) =>
        setErr(detailError(e, "prepaidDetail.loadError")),
      )
      .finally(() => setLoading(false));
  }, [company_id, prepaid_id]);

  useEffect(() => {
    if (!company_id) return;
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((xs) => setAccounts(xs || []))
      .catch(() => setAccounts([]));
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  // Initialize/restore the Apply form whenever the CURRENT prepaid's own
  // recognition attempt slot says to -- runs on prepaid_id changes (route
  // navigation -- PrepaidDetail.tsx is mounted once for the route and
  // reused across different prepaid_id values without remounting) and on
  // expenseAccounts finishing its own async load (so an account can
  // actually be matched/defaulted once options exist). Mirrors
  // DepositDetail.tsx's write-off restoration effect.
  //
  // - If THIS prepaid has a live ambiguous attempt (see
  //   shouldRestoreAmbiguousExpenseAttempt's docstring): restore its exact
  //   submitted date/amount/expense account, never fresh defaults --
  //   otherwise the very next submission would compute a different
  //   fingerprint and get blocked as an "edited retry".
  // - Otherwise (idle -- no retained attempt for this prepaid, whether
  //   because none was ever made or a prior one already resolved): default
  //   the expense account to the first available one, preserving the
  //   pre-existing behavior for a prepaid with no retained attempt.
  useEffect(() => {
    if (prepaid?.id == null) return;
    const prepaidIdNum = Number(prepaid.id);
    const attempt = getPrepaidExpenseAttempt(prepaidIdNum);

    if (shouldRestoreAmbiguousExpenseAttempt(attempt) && attempt.submittedPayload) {
      const payload = attempt.submittedPayload;
      setApplyDate(formatMMDDYYYYFromYMD(payload.date));
      setApplyAmount(payload.amount);
      const matchedAccount =
        expenseAccounts.find(
          (a) => String(a.id) === String(payload.expense_account_id),
        ) || null;
      if (matchedAccount) setExpenseAcc(matchedAccount);
      return;
    }

    if (!expenseAcc && expenseAccounts.length) setExpenseAcc(expenseAccounts[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepaid?.id, expenseAccounts.length]);

  // Seed memo draft when switching records
  useEffect(() => {
    setEditingMemo(false);
    setMemoError(null);
    setMemoDraft(prepaid?.memo || "");
  }, [prepaid?.id]);

  // Load history events
  useEffect(() => {
    if (!prepaid_id) return;

    let cancelled = false;
    setEventsLoading(true);
    setEventsError(null);

    fetchPrepaidEventsBestEffort(prepaid_id)
      .then((xs) => {
        if (cancelled) return;
        setEvents(xs || []);
        setEventsLoadedOnce(true);
      })
      .catch((e: any) => {
        if (cancelled) return;

        // If endpoint not added yet, don’t show “Not Found” under the section.
        if (e?.response?.status === 404) {
          setEvents([]);
          setEventsLoadedOnce(true);
          return;
        }

        const externalMessage = niceDetail(e?.response?.data?.detail) || e?.message;
        setEventsError(externalMessage
          ? { source: "external", text: externalMessage }
          : { source: "local" });
        setEventsLoadedOnce(true);
      })
      .finally(() => {
        if (cancelled) return;
        setEventsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [prepaid_id]);

  // Close delete modal on Escape
  useEffect(() => {
    if (!showDelete) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deleting) {
        setShowDelete(false);
        setDeleteError(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDelete, deleting]);

  const refresh = async () => {
    if (!company_id || !prepaid_id) return;
    const d = await fetchPrepaid(company_id, prepaid_id);
    setPrepaid(d);

    try {
      const xs = await fetchPrepaidEventsBestEffort(prepaid_id);
      setEvents(xs || []);
      setEventsLoadedOnce(true);
    } catch {
      // ignore
    }
  };

  const isClosedApplyDate = useMemo(() => {
    return isDateClosed(mdyToYmd(normalizeMDY(applyDate)), closedThroughDate);
  }, [applyDate, closedThroughDate]);

  const onApplyAmountBlur = () => {
    setApplyAmount(applyAmount.trim());
  };

  const onApplyDateBlur = () => {
    const normalized = normalizeMDY(applyDate);
    if (normalized) setApplyDate(normalized);
  };

  const handleApply = async () => {
    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `applying` state alone can't catch this
    // since it only updates on the next render.
    if (prepaidExpenseInFlightRef.current) return;

    setApplyError(null);
    if (!prepaid) return;
    if (!expenseAcc) return setApplyError(localError("depositDetail.selectExpenseError"));

    const amt = Number(
      String(applyAmount || "")
        .replace(/,/g, "")
        .trim(),
    );
    if (!Number.isFinite(amt) || amt <= 0)
      return setApplyError(localError("prepaidDetail.positiveAmount"));
    if (amt > remaining) return setApplyError(localError("depositDetail.amountExceeds"));

    const dateYmd = backendRejectsApplyDate
      ? undefined
      : mdyToYmd(normalizeMDY(applyDate));
    if (!backendRejectsApplyDate && !dateYmd)
      return setApplyError(localError("prepaidDetail.invalidDate"));

    const prepaidIdNum = Number(prepaid.id);

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether THIS PREPAID'S OWN slot (never any
    // other prepaid's) is a first attempt (mint a new key), an unchanged
    // retry of a still-ambiguous attempt (reuse its key), or an edited
    // retry of one (block, no network call). memo is always "" -- this
    // form has no memo input for Apply, matching the pre-existing behavior
    // of never sending one. The canonical payload's date is always the
    // resolved YMD value (even when backendRejectsApplyDate causes it not
    // to be sent) so the fingerprint stays a pure function of what the user
    // actually chose.
    const canonicalPayload = canonicalPrepaidExpense(
      prepaidIdNum,
      amt,
      expenseAcc.id,
      dateYmd || applyDate,
      "",
    );
    const fingerprint = fingerprintPrepaidExpense(canonicalPayload);
    const decision = decidePrepaidExpenseAttempt(
      getPrepaidExpenseAttempt(prepaidIdNum),
      canonicalPayload,
      fingerprint,
      randomUuidKey,
    );

    if (decision.kind === "blocked-mismatch") {
      setApplyError(localError("prepaidDetail.editedRetry"));
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- prepaidExpenseInFlightRef.current
      // already returns above before this point. Kept for correctness of
      // the pure decision function itself.
      return;
    }

    // Store the new attempt ONLY under this prepaid's own key -- every
    // other prepaid's slot in the map is untouched.
    prepaidExpenseAttemptsRef.current.set(prepaidIdNum, decision.attempt);
    prepaidExpenseInFlightRef.current = true;

    const basePayload: any = {
      expense_account_id: Number(expenseAcc.id),
      amount: amt,
    };
    const payload: any = {
      ...basePayload,
      idempotency_key: decision.idempotencyKey,
    };
    if (!backendRejectsApplyDate) payload.date = dateYmd;

    setApplying(true);
    try {
      await apiPost(`/prepaids/${prepaid.id}/expense`, payload);

      // Confirmed success (create or replay -- both return the same
      // response shape): clear ONLY this prepaid's slot back to idle so
      // its next recognition (e.g. a later distinct partial recognition)
      // gets a fresh key instead of leaking this one into it. Every other
      // prepaid's slot in the map is untouched.
      prepaidExpenseAttemptsRef.current.set(prepaidIdNum, resolvePrepaidExpenseSuccess());

      setApplyAmount("");
      await refresh();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = String(detail || "");

      // Pre-existing legacy fallback, unrelated to idempotency and left
      // untouched here: some historical backend version 422'd on an
      // explicit `date` field with "Input should be None". Not wired to
      // the idempotency attempt state (mirrors DepositDetail.tsx's
      // identical write-off fallback, which is also a bare second apiPost
      // call outside its own attempt state machine).
      if (
        e?.response?.status === 422 &&
        !backendRejectsApplyDate &&
        msg.includes("Input should be None")
      ) {
        setBackendRejectsApplyDate(true);
        prepaidExpenseAttemptsRef.current.set(prepaidIdNum, resolvePrepaidExpenseDefiniteRejection());
        try {
          const { date, ...noDate } = payload;
          await apiPost(`/prepaids/${prepaid.id}/expense`, noDate);
          setApplyAmount("");
          await refresh();
          return;
        } catch (e2: any) {
          if (isClosedPeriodError(e2)) {
            setApplyError({ source: "local", key: "sales.errors.closedSubmit", closedThroughDate });
            return;
          }

          setApplyError(detailError(e2, "prepaidDetail.applyError"));
          return;
        }
      }

      const failureClass = classifyPrepaidExpenseFailure(e, isClosedPeriodError);

      if (failureClass === "closed-period") {
        // Definite pre-commit rejection: assert_year_open raises before
        // any claim/business write commits, so nothing was persisted.
        // Clear this prepaid's slot so a corrected date can retry under a
        // new key.
        prepaidExpenseAttemptsRef.current.set(prepaidIdNum, resolvePrepaidExpenseDefiniteRejection());
        setApplyError({ source: "local", key: "sales.errors.closedSubmit", closedThroughDate });
        return;
      }

      if (failureClass === "idempotency-conflict") {
        // Definite: this key is permanently bound to a different,
        // already-completed request. Retrying under it can never succeed
        // for this payload -- clear this prepaid's slot so the next
        // attempt mints a fresh one.
        prepaidExpenseAttemptsRef.current.set(prepaidIdNum, resolvePrepaidExpenseDefiniteRejection());
        setApplyError(localError("prepaidDetail.conflict"));
        return;
      }

      if (failureClass === "definite-rejection") {
        prepaidExpenseAttemptsRef.current.set(prepaidIdNum, resolvePrepaidExpenseDefiniteRejection());
        setApplyError(detailError(e, "prepaidDetail.applyError"));
        return;
      }

      // Ambiguous (network failure, timeout, unreadable response, HTTP
      // 500, or anything else unrecognized): retain the key, fingerprint,
      // and submitted payload under THIS prepaid's own slot so an
      // unchanged retry reuses the same key and safely replays instead of
      // risking a duplicate. Every other prepaid's own slot remains
      // independently usable.
      prepaidExpenseAttemptsRef.current.set(
        prepaidIdNum,
        resolvePrepaidExpenseAmbiguousFailure(getPrepaidExpenseAttempt(prepaidIdNum)),
      );
      setApplyError(detailError(e, "prepaidDetail.applyError"));
    } finally {
      prepaidExpenseInFlightRef.current = false;
      setApplying(false);
    }
  };

  const handleSaveMemo = async () => {
    if (!prepaid) return;
    setMemoError(null);
    setMemoSaving(true);
    try {
      await apiPost(`/prepaids/${prepaid.id}/memo`, {
        memo: memoDraft.trim() || "",
      });
      await refresh();
      setEditingMemo(false);
    } catch (e: any) {
      setMemoError(detailError(e, "depositDetail.memoError"));
    } finally {
      setMemoSaving(false);
    }
  };

  const openDelete = () => {
    setDeleteError(null);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    setDeleteError(null);
    if (!prepaid) return;

    setDeleting(true);
    try {
      await apiDelete(`/prepaids/${prepaid.id}`);
      navigate(companyRoute(company_id!, "/recognition/prepaids"));
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setDeleteError(localError("sales.errors.closedEdit"));
        return;
      }

      setDeleteError(detailError(e, "prepaidDetail.voidError"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen bg-gray-50 ${A.body} flex items-center justify-center text-gray-500`}
      >
        {t("purchases.edit.loading")}
      </div>
    );
  }

  if (err) {
    return (
      <div
        className={`min-h-screen bg-gray-50 ${A.body} flex items-center justify-center text-red-500 px-4`}
      >
        {renderError(err)}
      </div>
    );
  }

  if (!prepaid) {
    return (
      <div
        className={`min-h-screen bg-gray-50 ${A.body} flex items-center justify-center text-gray-500 px-4`}
      >
        {t("prepaidDetail.notFound")}
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-6`}
    >
      {/* Delete Modal (SaleDetail-style) */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
          onClick={() => {
            if (deleting) return;
            setShowDelete(false);
            setDeleteError(null);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-md p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[15px] font-semibold text-gray-700">
              {t("prepaidDetail.voidTitle")}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              <Trans t={t} i18nKey="prepaidDetail.voidBody" components={{ emphasis: <span className="font-medium" /> }} />
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="secondary"
                type="button"
                className={btnSmSecondary}
                onClick={() => {
                  if (deleting) return;
                  setShowDelete(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="primary"
                type="button"
                className={btnSmDanger}
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? t("prepaidDetail.voiding") : t("prepaidDetail.void")}
              </Button>
            </div>

            {deleteError && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-[13px] text-red-700 whitespace-pre-wrap">
                {renderError(deleteError)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-6xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() =>
            navigate(companyRoute(company_id!, "/recognition/prepaids"))
          }
          className={`${A.iconBtn} hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <div className="flex-1 text-center pointer-events-none">
          <h2 className={A.title}>{t("prepaidDetail.title")}</h2>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id!, "/"))}
          className={`${A.iconBtn} hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Card */}
      <div className={`${A.card} w-full max-w-3xl mx-auto px-6 py-5`}>
        <PurchaseInfoHeader
          prepaidRowLabels={{ date: t("sales.common.date"), supplier: t("purchases.header.supplier"),
            outstanding: t("prepaidDetail.outstanding"), total: t("sales.common.total") }}
          date={prepaid.date}
          vendor={prepaid.vendor_name || undefined}
          email={undefined}
          statusBubbles={statusBubbles}
          outstanding={!isClosed && remaining > 0 ? remaining : 0}
          dueDate={undefined}
          total={original}
          isPaid={isClosed}
          edited={false}
          lastEditedAt={undefined}
        />

        {/* Summary table (Desktop) */}
        <div className="hidden sm:block mb-5">
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className={`w-full table-fixed ${A.body}`}>
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>

              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    {t("directory.vendors.singular")}
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                    {t("purchases.detail.paymentAccount")}
                  </th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 tabular-nums">
                    {t("sales.common.amount")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{prepaid.vendor_name || "—"}</td>
                    <td className="px-3 py-2">{line.account || "—"}</td>
                    <td className="px-3 py-2 text-right font-medium whitespace-nowrap tabular-nums">
                      {money(line.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary cards (Mobile) */}
        <div className="block sm:hidden space-y-3 mb-5">
          {lines.map((line) => (
            <div
              key={line.id}
              className="rounded-2xl border border-gray-200 shadow-sm bg-white px-4 py-3 text-[13px] text-gray-700"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-500 mb-1">
                    {t("directory.vendors.singular")}
                  </div>
                  <div className="break-words">
                    {prepaid.vendor_name || "—"}
                  </div>
                </div>

                <div className="text-right tabular-nums font-medium whitespace-nowrap">
                  {money(line.amount)}
                </div>
              </div>

              <div className="mt-2">
                <div className="text-xs text-gray-500 mb-1">
                  {t("purchases.detail.paymentAccount")}
                </div>
                <div className="break-words">{line.account || "—"}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Total / Remaining */}
        <div className="mb-1 text-right tabular-nums">
          <span className="text-[15px] font-semibold text-gray-700">
            {t("sales.common.total")}: {money(original)}
          </span>
        </div>
        <div className="mb-6 text-right tabular-nums">
          <span className="text-xs text-gray-500">{t("spendingDeposits.remaining")}</span>
          <span className="ml-2 text-[13px] font-medium text-gray-700">
            {money(remaining)}
          </span>
        </div>

        {/* History */}
        {showHistorySection && (
          <div className="mb-6">
            <div className={`${A.label} mb-2`}>{t("prepaidHistory.title")}</div>

            {eventsLoading ? (
              <div className="text-xs text-gray-500">{t("depositDetail.loadingHistory")}</div>
            ) : eventsError ? (
              <div className="text-xs text-red-500">{eventsError.source === "local"
                ? t("prepaidHistory.loadError")
                : eventsError.text}</div>
            ) : historyRows.length === 0 ? null : (
              <>
                {/* Desktop */}
                <div className="hidden sm:block">
                  <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                    <table className={`w-full table-fixed ${A.body}`}>
                      <colgroup>
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "40%" }} />
                        <col style={{ width: "20%" }} />
                      </colgroup>

                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                            {t("sales.common.date")}
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                            {t("depositDetail.action")}
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                            {t("generalJournal.account")}
                          </th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 tabular-nums">
                            {t("sales.common.amount")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {historyRows.map((r) => (
                          <tr key={r.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.date}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {r.action}
                            </td>
                            <td className="px-3 py-2">
                              <div
                                className="whitespace-normal break-words"
                                style={
                                  {
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  } as any
                                }
                              >
                                {r.displayAccount}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap">
                              {money(r.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile */}
                <div className="block sm:hidden space-y-2">
                  {historyRows.map((r) => (
                    <div
                      key={r.id}
                      className="rounded-2xl border border-gray-200 shadow-sm bg-white px-4 py-3 text-[13px] text-gray-700"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">
                            {t("sales.common.date")}
                          </div>
                          <div>{r.date}</div>
                        </div>

                        <div className="text-right tabular-nums">
                          <div className="text-xs text-gray-500 mb-0.5">
                            {t("sales.common.amount")}
                          </div>
                          <div className="font-medium">{money(r.amount)}</div>
                        </div>
                      </div>

                      <div className="mb-2">
                        <div className="text-xs text-gray-500 mb-0.5">
                          {t("depositDetail.action")}
                        </div>
                        <div>{r.action}</div>
                      </div>

                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">
                          {t("generalJournal.account")}
                        </div>
                        <div className="break-words">{r.displayAccount}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Apply */}
        {canApply && (
          <div className="bg-gray-50 rounded-2xl px-4 py-3 shadow-sm mb-6 ring-1 ring-black/5">
            <div className={`${A.label} mb-2`}>{t("prepaidDetail.applyTitle")}</div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={`${A.label} block mb-1`}>{t("sales.common.date")}</label>
                <input
                  type="text"
                  value={applyDate}
                  onChange={(e) => setApplyDate(normalizeMDY(e.target.value))}
                  onBlur={onApplyDateBlur}
                  placeholder="MM-DD-YYYY"
                  disabled={backendRejectsApplyDate}
                  className={[
                    A.inputBase,
                    "text-left tabular-nums",
                    backendRejectsApplyDate
                      ? "opacity-60 cursor-not-allowed"
                      : "",
                  ].join(" ")}
                />

                {isClosedApplyDate && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {t("sales.errors.closedWarning")}
                  </div>
                )}

                {backendRejectsApplyDate ? (
                  <div className="text-xs text-gray-500 mt-1">
                    {t("prepaidDetail.legacyDateIgnored")}
                  </div>
                ) : null}
              </div>

              <div>
                <label className={`${A.label} block mb-1`}>
                  {t("depositDetail.expenseAccount")}
                </label>
                <AppleComboBox<Account>
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  options={expenseAccounts}
                  value={expenseAcc}
                  onChange={setExpenseAcc}
                  getOptionSearchText={(a) => a.name}
                  getOptionLabel={(a) => localizedAccountDisplayName(uniqueDisplayAccount(accounts, a.id, a.name), t, a.name)}
                  placeholder={
                    expenseAccounts.length
                      ? t("depositDetail.selectExpenseAccount")
                      : t("depositDetail.noExpenseAccounts")
                  }
                />
              </div>

              <div>
                <label className={`${A.label} block mb-1`}>{t("sales.common.amount")}</label>
                <AppleNumberInput
                  className="text-right tabular-nums"
                  value={applyAmount}
                  onChange={(e) => setApplyAmount(e.target.value)}
                  onBlur={onApplyAmountBlur}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                onClick={handleApply}
                disabled={
                  applying ||
                  !expenseAcc ||
                  !applyAmount ||
                  isNaN(
                    Number(
                      String(applyAmount || "")
                        .replace(/,/g, "")
                        .trim(),
                    ),
                  ) ||
                  Number(
                    String(applyAmount || "")
                      .replace(/,/g, "")
                      .trim(),
                  ) <= 0 ||
                  Number(
                    String(applyAmount || "")
                      .replace(/,/g, "")
                      .trim(),
                  ) > remaining
                }
                variant="primary"
                className={btnSmPrimary}
              >
                {applying ? t("directory.common.savingDots") : t("prepaidHistory.apply")}
              </Button>
            </div>

            <div className="min-h-[18px] mt-2">
              {applyError ? (
                <div className="text-xs text-red-500 font-medium">
                  {renderError(applyError)}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ===== Memo (STANDARD) ===== */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm px-4 py-3 mb-6 relative">
          <div className={`${A.label} mb-1`}>{t("sales.common.memo")}</div>

          {/* Memo */}
          {!editingMemo ? (
            <MemoSection
              label={t("sales.common.memo")}
              editAriaLabel={t("sales.common.editMemo")}
              mode="view"
              variant="card"
              value={prepaid.memo || ""}
              emptyText={t("sales.detail.noMemo")}
              className="mb-6"
              onEditClick={() => {
                setEditingMemo(true);
                setMemoDraft(prepaid.memo || "");
                setMemoError(null);
              }}
            />
          ) : (
            <div className="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
              <MemoSection
                mode="edit"
                variant="plain"
                label={t("sales.common.memo")}
                value={memoDraft}
                onChange={setMemoDraft}
                rows={2}
                disabled={memoSaving}
                placeholder={t("sales.common.memoPlaceholder")}
              />

              <div className="flex gap-3 justify-end mt-3">
                <Button
                  variant="secondary"
                  type="button"
                  className={btnSmSecondary}
                  onClick={() => {
                    setEditingMemo(false);
                    setMemoDraft(prepaid.memo || "");
                    setMemoError(null);
                  }}
                  disabled={memoSaving}
                >
                  {t("directory.common.cancel")}
                </Button>

                <Button
                  variant="primary"
                  type="button"
                  className={btnSmPrimary}
                  onClick={handleSaveMemo}
                  disabled={memoSaving}
                >
                  {memoSaving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
                </Button>
              </div>

              <div className="min-h-[18px] mt-2">
                {memoError ? (
                  <div className="text-xs text-red-500 font-medium">
                    {renderError(memoError)}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <Button
            variant="secondary"
            type="button"
            className={btnSmSecondary}
            onClick={openDelete}
            disabled={!deleteAvailable || deleting}
          >
            {t("prepaidDetail.void")}
          </Button>
        </div>

        {!deleteAvailable && !deleting ? (
          <div className="text-xs text-gray-400 mt-2 text-right">
            {t("prepaidDetail.voidAvailability")}
          </div>
        ) : null}
      </div>
    </div>
  );
}
